/**
 * Site-wide profile/avatar image resolver.
 *
 * Different API surfaces expose different image fields:
 *   • `users.profile_image`            — canonical, when the user has explicitly uploaded an avatar
 *   • `talent_profiles.headshot_url`   — talent's primary headshot (often the only one Talent uploads)
 *   • `companies/agencies.logo_url`    — company / agency logos
 *   • participant objects in conversations expose `profile_image`
 *   • directory cards expose `headshot_url`, `profile_image`, or `logo_url`
 *   • notifications/messages embed denormalised `actor_avatar` / `sender_profile_image`
 *
 * Calling components used to read a single field (commonly `profile_image`),
 * which silently broke for users who had a headshot in their talent profile
 * but an empty top-level `profile_image`.
 *
 * This helper checks the same fields the backend actually returns, in
 * priority order, and returns the first truthy non-blank URL.
 *
 * Important:
 *   Returns `null` when nothing usable is found OR when the value looks like
 *   an old stock/default/demo avatar. Call-sites should then render initials
 *   in AvatarFallback, not a real-person placeholder image.
 *
 * The priority list is order-stable:
 *   1.  Direct upload of an explicit "profile image" wins over derived
 *       headshots, so admin-set or user-set avatars always show first.
 *   2.  Headshot / talent imagery comes next (most Talent users only set
 *       this).
 *   3.  Logo fields are last — they're used by company / agency cards
 *       where we still want a graphic over initials.
 *   4.  Generic `image_url` / `display_image` come last as a final
 *       safety net for new endpoints that may add denormalised fields.
 */

const FIELDS_IN_PRIORITY = [
  // Explicit avatar / profile image fields
  'profile_image_url',
  'profile_image',
  'avatar_url',
  'avatar',

  // Headshot fields (talent profiles)
  'headshot_url',
  'headshot',

  // Logo fields (companies, agencies, services)
  'logo_url',
  'logo',

  // Imported listing fallback — admin-set, lower priority than any
  // user-uploaded asset so a real user upload always wins.
  'imported_image_url',

  // Generic fallbacks
  'image_url',
  'display_image',
];

/**
 * String values that mean "no image" but sometimes leak through APIs/forms.
 */
const EMPTY_IMAGE_VALUES = new Set([
  '',
  'null',
  'undefined',
  'none',
  'n/a',
  'na',
  'false',
  '0',
]);

/**
 * Known old/default/demo/placeholder image markers.
 *
 * Keep this list conservative. Do NOT block broad words like "user" or
 * "profile" because real uploaded files may contain those. These markers
 * target placeholder-style filenames/paths only.
 */
const BLOCKED_DEFAULT_IMAGE_MARKERS = [
  'default-avatar',
  'default_user',
  'default-user',
  'default-profile',
  'default_profile',
  'fallback-avatar',
  'fallback_user',
  'fallback-user',
  'avatar-placeholder',
  'profile-placeholder',
  'user-placeholder',
  'placeholder-avatar',
  'placeholder-profile',
  'placeholder-user',
  'stock-avatar',
  'stock_profile',
  'stock-profile',
  'sample-avatar',
  'sample-profile',
  'demo-avatar',
  'demo-profile',
  'demo-user-avatar',
  'test-avatar',
  'test-profile',
  'black-guy',
  'black_guy',
  'blackman',
  'black-man',
  'old-default-avatar',
  'old-default-profile',
];

/**
 * Some image services or seed scripts can store a full URL with query params.
 * Normalising the value lets us check markers safely without losing the
 * original URL when it is usable.
 */
function normaliseImageValue(value) {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();

  if (EMPTY_IMAGE_VALUES.has(lower)) return null;

  return trimmed;
}

/**
 * Returns true only for image values we should actually render.
 *
 * This deliberately rejects old stock/default/demo images so new users without
 * uploads fall through to branded initials instead of being assigned a random
 * real-person photo.
 */
export function isUsableProfileImageUrl(value) {
  const url = normaliseImageValue(value);
  if (!url) return false;

  const lower = url.toLowerCase();

  if (BLOCKED_DEFAULT_IMAGE_MARKERS.some((marker) => lower.includes(marker))) {
    return false;
  }

  // Avoid rendering broken relative placeholders that are not real files.
  if (
    lower === '/avatar.png'
    || lower === '/default.png'
    || lower === '/profile.png'
    || lower === '/user.png'
    || lower === '/images/avatar.png'
    || lower === '/images/default.png'
    || lower === '/assets/avatar.png'
    || lower === '/assets/default.png'
  ) {
    return false;
  }

  // Allow normal app uploads, absolute URLs, local asset paths, blob URLs and
  // data URLs. If a backend returns a clean relative upload path, keep it.
  return true;
}

function pickFromObject(obj) {
  if (!obj || typeof obj !== 'object') return null;

  for (const key of FIELDS_IN_PRIORITY) {
    const value = obj[key];

    if (typeof value === 'string' && isUsableProfileImageUrl(value)) {
      return value.trim();
    }
  }

  return null;
}

/**
 * Resolve a usable profile/avatar image URL from a user/profile/participant object.
 * Accepts either a flat user-ish object or one of the common nested shapes:
 *   resolveProfileImage(user)
 *   resolveProfileImage({ user: { profile_image: ... } })
 *   resolveProfileImage({ profile: { headshot_url: ... } })
 *
 * @param  {object} obj A user, profile, participant, or wrapper object.
 * @returns {string|null} A usable URL, or null when none is found.
 */
export function resolveProfileImage(obj) {
  if (!obj) return null;

  // Direct hit
  const direct = pickFromObject(obj);
  if (direct) return direct;

  // Common nested shapes. Keep this central so components do not need to know
  // whether the API returned a user, profile, talent profile or participant.
  const nested =
    pickFromObject(obj.user)
    || pickFromObject(obj.profile)
    || pickFromObject(obj.talent)
    || pickFromObject(obj.talent_profile)
    || pickFromObject(obj.company)
    || pickFromObject(obj.agency)
    || pickFromObject(obj.participant);

  return nested || null;
}

/**
 * Build initials from a name string for the fallback bubble.
 * "Wayne Dissington" -> "WD", "Olivia" -> "OL"
 */
export function initialsFor(name) {
  const s = (name || '').trim();

  if (!s) return 'CC';

  const parts = s.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  return s.slice(0, 2).toUpperCase();
}

/**
 * Pull a sensible display name from the mixed user/profile shapes used across
 * the app. Useful for AvatarFallback call-sites.
 */
export function displayNameFor(obj) {
  if (!obj || typeof obj !== 'object') return '';

  return (
    obj.name
    || obj.full_name
    || obj.display_name
    || obj.username
    || obj.email
    || obj.user?.name
    || obj.user?.full_name
    || obj.user?.display_name
    || obj.profile?.name
    || obj.profile?.full_name
    || obj.talent?.name
    || obj.talent_profile?.name
    || ''
  );
}

/**
 * Standard fallback text for branded avatar bubbles.
 * Components should use:
 *
 *   <AvatarImage src={resolveProfileImage(user)} />
 *   <AvatarFallback>{avatarFallbackText(user)}</AvatarFallback>
 *
 * This keeps new users looking like proper CCUK accounts without ever showing
 * an unrelated real-person default image.
 */
export function avatarFallbackText(obj) {
  return initialsFor(displayNameFor(obj));
}
